import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { t } from '@/i18n';
import { MicStatusPill } from '../MicStatusPill';
import { BottomNav } from './BottomNav';
import { BrandMark } from './BrandMark';
import './layout.css';

interface AppShellProps {
  children: ReactNode;
  /** Screen title. Omit to show the brand lockup instead. */
  title?: string;
  showBack?: boolean;
  /**
   * Replaces the default "go back in history" behaviour. An active session
   * passes its own handler so that leaving saves what was practised instead of
   * throwing it away.
   */
  onBack?: () => void;
  /** Replaces the microphone pill — used by the active-session header. */
  headerRight?: ReactNode;
  showNav?: boolean;
  /** Removes the standard page padding, for full-bleed screens. */
  bare?: boolean;
}

export function AppShell({
  children,
  title,
  showBack = false,
  onBack,
  headerRight,
  showNav = true,
  bare = false,
}: AppShellProps) {
  const navigate = useNavigate();

  return (
    <div className="shell">
      <header className="shell__header">
        <div className="shell__header-inner">
          <div className="shell__header-left">
            {showBack && (
              <button
                type="button"
                className="shell__back"
                onClick={() => (onBack ? onBack() : navigate(-1))}
                aria-label={t('common.back')}
              >
                <ArrowLeft size={24} aria-hidden="true" />
              </button>
            )}
            {title ? <h1 className="shell__title">{title}</h1> : <BrandMark />}
          </div>
          <div className="shell__header-right">{headerRight ?? <MicStatusPill />}</div>
        </div>
      </header>

      <main className={`shell__main${bare ? ' shell__main--bare' : ''}${showNav ? '' : ' shell__main--nonav'}`}>
        <div className="shell__column">{children}</div>
      </main>

      {showNav && <BottomNav />}
    </div>
  );
}
